/**
 * The paste window's footer (27/10/2026: one quiet line): how pasted things arrive — or, when
 * Paste can't run yet, why in a few words (the first country not ready; the full reasons are in
 * that country's status card) — then Cancel and Paste, into every country at once.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { GroupCopyReview } from '@trafficpulse/shared';

import { Button, DrawerFooter } from '@/components/ui';
import { pasteAllLabel } from '@/features/drop-generator/components/group-copy/dialog/model/paste-summary';
import { pasteOffReason, type TargetReview } from '@/features/drop-generator/components/group-copy/dialog/model/targets/target-reviews';
import { cn } from '@/lib/utils';

interface Props {
  all: TargetReview[];
  nameOf: (countryKey: string) => string;
  state: { pasting: boolean; canPaste: boolean };
  onCancel: () => void;
  onPaste: () => void;
}

export const PasteFooter: FC<Props> = ({ all, nameOf, state, onCancel, onPaste }) => {
  const reason = state.canPaste ? pasteOffReason(all, nameOf) : 'Pasting needs the Automation manage permission.';
  const reviews = all.map((t) => t.review).filter((r): r is GroupCopyReview => Boolean(r));
  const reading = reason?.startsWith('Reading') ?? false;
  return (
    <DrawerFooter className="flex-wrap justify-between gap-x-4 gap-y-2 px-5 py-3">
      <span className={cn('min-w-0 flex-1 basis-52 text-xs', reason && !reading ? 'text-warning' : 'text-muted-foreground')}>
        {reason ?? 'Containers arrive switched off · auto-send off'}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="button" size="sm" className="min-w-[8.5rem]" disabled={reason !== null || state.pasting} title={reason ?? undefined} onClick={onPaste}>
          {state.pasting ? 'Pasting…' : pasteAllLabel(reviews)}
        </Button>
      </span>
    </DrawerFooter>
  );
};
